import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Loader2, Mail, Smartphone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { useToast } from '../components/ui/use-toast';
import {
  notificationPreferencesService,
  type NotificationPreference,
} from '../services/notificationPreferences';
import { TimerNotificationsCard } from '../components/notification-settings/TimerNotificationsCard';
import { PushNotificationToggle } from '../components/push-notification-toggle';

type Channel = 'push_enabled' | 'email_enabled';

// Each global notification type and the channels a user can control for it.
// profile_completion reminders, the day-after-a-crag follow-up, the daily
// digest and product announcements are email-only (no push channel).
const NOTIFICATION_TYPES: { type: string; channels: Channel[] }[] = [
  { type: 'user_climb', channels: ['push_enabled', 'email_enabled'] },
  { type: 'friend_climb', channels: ['push_enabled', 'email_enabled'] },
  { type: 'mention', channels: ['push_enabled', 'email_enabled'] },
  { type: 'send_comment', channels: ['push_enabled', 'email_enabled'] },
  { type: 'send_thread_comment', channels: ['push_enabled', 'email_enabled'] },
  { type: 'new_follower', channels: ['push_enabled', 'email_enabled'] },
  { type: 'profile_completion', channels: ['email_enabled'] },
  { type: 'crag_visit_follow_up', channels: ['email_enabled'] },
  { type: 'weekly_check_in', channels: ['push_enabled', 'email_enabled'] },
  { type: 'daily_climb_digest', channels: ['email_enabled'] },
  { type: 'product_announcement', channels: ['email_enabled'] },
];

const ALL_CHANNELS: Channel[] = ['push_enabled', 'email_enabled'];

export default function NotificationSettingsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    notificationPreferencesService
      .getPreferences()
      .then(setPreferences)
      .catch(() =>
        toast({ title: t('notificationSettings.error'), variant: 'destructive' })
      )
      .finally(() => setLoading(false));
  }, [t, toast]);

  const handleToggle = async (
    type: string,
    channel: 'push_enabled' | 'email_enabled',
    value: boolean
  ) => {
    const key = `${type}-${channel}`;
    setSaving(key);

    const updated = preferences.map((p) =>
      p.notification_type === type ? { ...p, [channel]: value } : p
    );
    setPreferences(updated);

    try {
      const pref = updated.find((p) => p.notification_type === type)!;
      await notificationPreferencesService.updatePreferences([pref]);
      toast({ title: t('notificationSettings.saved') });
    } catch {
      setPreferences(preferences);
      toast({ title: t('notificationSettings.error'), variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const getPref = (type: string): NotificationPreference | undefined =>
    preferences.find((p) => p.notification_type === type);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">{t('notificationSettings.loading')}</span>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('notificationSettings.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('notificationSettings.description')}</p>
      </div>

      {/* Whether this device gets anything at all, for this account: the
          switches below only choose what. */}
      <PushNotificationToggle showAsCard source="settings_toggle" />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t('notificationSettings.title')}
          </CardTitle>
          <CardDescription>{t('notificationSettings.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {/* Header row */}
            <div className="grid grid-cols-[1fr_auto_auto] gap-4 items-center pb-3 border-b text-sm font-medium text-muted-foreground">
              <span />
              <span className="flex items-center gap-1 w-16 justify-center">
                <Smartphone className="h-4 w-4" />
                {t('notificationSettings.push')}
              </span>
              <span className="flex items-center gap-1 w-16 justify-center">
                <Mail className="h-4 w-4" />
                {t('notificationSettings.email')}
              </span>
            </div>

            {NOTIFICATION_TYPES.map(({ type, channels }) => {
              const pref = getPref(type);

              return (
                <div
                  key={type}
                  className="grid grid-cols-[1fr_auto_auto] gap-4 items-center py-3 border-b last:border-0"
                >
                  <span className="font-medium">
                    {t(`notificationSettings.types.${type}`)}
                  </span>
                  {ALL_CHANNELS.map((channel) => (
                    <div key={channel} className="flex w-16 justify-center">
                      {channels.includes(channel) && (
                        <Switch
                          checked={pref?.[channel] ?? false}
                          onCheckedChange={(v) => handleToggle(type, channel, v)}
                          disabled={saving === `${type}-${channel}`}
                          aria-label={`${t(`notificationSettings.types.${type}`)} ${
                            channel === 'push_enabled' ? 'push' : 'email'
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <TimerNotificationsCard />
    </div>
  );
}
